import './Edit.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  Placeholder,
  ScrollView,
  SegmentedControl,
  Tabs,
  TabsItem,
} from '$uikit';
import { BottomBar } from '$uikit';
import Birthday from '$shared/components/Birthday/Birthday';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { toastActions } from '$store/toast';
import { USER_NAME_REGEX } from '$shared/constants';
import { profileActions } from '$store/profile';
import { IcPlusCircle28 } from '$assets';
import { EditLinkProps } from '$core/Edit/Edit.interface';
import { _confirm, classNames, rnbr } from '$utils';
import { ButtonIcons } from '$assets/mapping';
import { Api } from '$api';
import { LinkModel, Sex } from '$store/models';
import { Loader } from '$shared/components';
import { getLinksRaw } from '$shared/components/Linkify/Linkify';
import { ColorsEnum } from '$shared/constants/colors';

const MAX_BIO_LEN = 300;
const Colors = Object.values(ColorsEnum);
const Icons = [
  'link',
  'disable',
  'instagram',
  'telegram',
  'vk',
  'boosty',
  'facebook',
  'twitter',
  'onlyfans',
  'fansly',
];

export function Edit() {
  const user = useCurrentUserInfo();
  const dispatch = useDispatch();
  const t = useTranslator();

  const [activeTab, setActiveTab] = useState('general');
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [sex, setSex] = useState<Sex>(user.sex);
  const [birthday, setBirthday] = useState(user.bDate);
  const [bio, setBio] = useState(user.bio || '');
  const [editLinkId, setEditLinkId] = useState(0);
  const [linksState, setLinksState] = useState('loading');
  const [links, setLinks] = useState<LinkModel[]>([]);
  const [editLink, setEditLink] = useState<LinkModel | null>(null);

  const loadLinks = useCallback(() => {
    setLinksState('loading');
    Api.get('/profile/links')
      .then((resp) => {
        const data: any = resp;
        setLinksState('');
        setLinks(data.links);
      })
      .catch(() => {
        setLinksState('failed');
      });
  }, []);

  useEffect(() => {
    loadLinks();
  }, []);

  const handleEditLink = useCallback(
    (linkId: number) => () => {
      if (linkId > 0) {
        for (let link of links) {
          if (link.id === linkId) {
            setEditLink({ ...link });
            break;
          }
        }
      } else {
        setEditLink({ id: linkId, text: '', url: '', color: '', icon: '' });
      }

      setEditLinkId(linkId);
    },
    [links],
  );

  const handleFirstName = useCallback(
    ({ target }) => {
      setFirstName(target.value);
    },
    [setFirstName],
  );

  const handleLastName = useCallback(
    ({ target }) => {
      setLastName(target.value);
    },
    [setLastName],
  );

  const handleBio = useCallback(
    ({ target }) => {
      setBio(target.value.slice(0, MAX_BIO_LEN));
    },
    [setBio],
  );

  const handleSex = useCallback(
    (value: any) => {
      setSex(value);
    },
    [setSex],
  );

  const handleBirthday = useCallback(
    (newBirthday) => {
      setBirthday(newBirthday);
    },
    [setBirthday],
  );

  const handleSave = useCallback(() => {
    if (firstName.trim().length < 2) {
      return dispatch(toastActions.setToastFail(t('reg_name_short')));
    }

    if (firstName.trim().length > 16) {
      return dispatch(toastActions.setToastFail(t('reg_name_long')));
    }

    if (!firstName.trim().match(USER_NAME_REGEX)) {
      return dispatch(toastActions.setToastFail(t('reg_name_wrong')));
    }

    if (lastName.trim().length > 16) {
      return dispatch(toastActions.setToastFail(t('reg_last_name_long')));
    }

    if (!!lastName.length && !lastName.trim().match(USER_NAME_REGEX)) {
      return dispatch(toastActions.setToastFail(t('reg_last_name_wrong')));
    }

    if (sex !== Sex.FEMALE && sex !== Sex.MALE) {
      return dispatch(toastActions.setToastFail(t('reg_select_gender')));
    }

    dispatch(
      profileActions.editProfile({
        firstName,
        lastName,
        sex,
        birthday,
        countryId: 0,
        cityId: 0,
        bio,
      }),
    );
  }, [dispatch, firstName, lastName, sex, birthday, bio, t]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);

  const availBioLen = useMemo(() => {
    return MAX_BIO_LEN - bio.length;
  }, [bio]);

  const handleEditLinkBack = useCallback(() => {
    setEditLinkId(0);
  }, []);

  const handleUpdateLinkInfo = useCallback(
    (link: LinkModel) => {
      const linksCopy = [...links];
      let exist = false;
      for (let i = 0; i < linksCopy.length; i++) {
        if (linksCopy[i].id === link.id) {
          linksCopy[i] = link;
          exist = true;
          break;
        }
      }

      if (!exist) {
        linksCopy.push(link);
      }

      setLinks(linksCopy);
    },
    [links],
  );

  const handleRemoveLinkInfo = useCallback(
    (linkId: number) => {
      const linksCopy = [...links];
      for (let i = 0; i < linksCopy.length; i++) {
        if (linksCopy[i].id === linkId) {
          linksCopy.splice(i, 1);
          break;
        }
      }

      setLinks(linksCopy);
    },
    [links],
  );

  if (editLinkId !== 0) {
    return (
      <EditLink
        handleBack={handleEditLinkBack}
        linkId={editLinkId}
        updateLinkInfo={handleUpdateLinkInfo}
        link={editLink!}
        removeLinkInfo={handleRemoveLinkInfo}
      />
    );
  }

  function renderContent() {
    if (activeTab === 'general') {
      return (
        <Form>
          <FormItem title={t('reg_name')}>
            <Input
              placeholder={t('reg_enter_name')}
              value={firstName}
              onChange={handleFirstName}
            />
          </FormItem>
          <FormItem title={t('reg_last_name')}>
            <Input
              placeholder={t('reg_enter_last_name')}
              value={lastName}
              onChange={handleLastName}
            />
          </FormItem>
          <FormItem
            title={t('edit_bio')}
            description={t('symbols_limit', { count: availBioLen })}
          >
            <Input
              placeholder={t('edit_enter_bio')}
              value={bio}
              onChange={handleBio}
              multiline
            />
          </FormItem>
          <FormItem title={t('reg_gender')}>
            <SegmentedControl
              options={[
                { label: t('reg_gender_female'), value: 1 },
                { label: t('reg_gender_male'), value: 2 },
              ]}
              defaultValue={sex}
              onChange={handleSex}
            />
          </FormItem>
          <FormItem title={t('reg_birthday')}>
            <Birthday birthday={birthday} onChange={handleBirthday} />
          </FormItem>
        </Form>
      );
    } else if (activeTab === 'interests') {
      return (
        <Form>
          <FormItem title="Деятельность">
            <Input multiline />
          </FormItem>
          <FormItem title="Интересы">
            <Input multiline />
          </FormItem>
          <FormItem title="Любимая музыка">
            <Input multiline />
          </FormItem>
          <FormItem title="Любимые фильмы">
            <Input multiline />
          </FormItem>
          <FormItem title="Любимые телешоу">
            <Input multiline />
          </FormItem>
          <FormItem title="Любимые книги">
            <Input multiline />
          </FormItem>
          <FormItem title="Любимые игры">
            <Input multiline />
          </FormItem>
          <FormItem title="Любимые цитаты">
            <Input multiline />
          </FormItem>
          <FormItem title="О себе">
            <Input multiline />
          </FormItem>
        </Form>
      );
    } else if (activeTab === 'links') {
      if (linksState === 'loading') {
        return <Loader isWrapped />;
      }

      if (linksState === 'failed') {
        return (
          <Placeholder
            title={t('failed_title')}
            actions={
              <Button size="small" onClick={loadLinks}>
                {t('failed_button')}
              </Button>
            }
          >
            {t('failed_caption')}
          </Placeholder>
        );
      }

      return (
        <div className="Edit__links">
          <div className="Edit__links__description">
            {rnbr(t('edit_link_links_description'))}
            <a
              href={`https://tpc.fan/${user.domain ? user.domain : `id${user.id}`}`}
              className="Edit__links__profile_link"
              target="_blank"
              rel="noreferrer"
            >
              tpc.fan/{user.domain ? user.domain : `id${user.id}`}
            </a>
          </div>
          <List>
            {links.length < 10 && (
              <ListItem
                before={
                  <div className="Edit__add_link_icon">
                    <IcPlusCircle28 />
                  </div>
                }
                onClick={handleEditLink(-1)}
              >
                {t('edit_links_add_btn')}
              </ListItem>
            )}
            {links.map((link) => {
              const Icon = ButtonIcons[link.icon];
              return (
                <ListItem
                  key={link.id}
                  icon={<Icon />}
                  description={link.url}
                  onClick={handleEditLink(link.id)}
                  before={
                    <div
                      className={classNames({
                        Edit__link_color: true,
                        [link.color]: true,
                      })}
                    />
                  }
                >
                  {link.text || `#${link.id}`}
                </ListItem>
              );
            })}
          </List>
        </div>
      );
    }
  }

  return (
    <>
      <Header showCloseButton>{t('other_edit')}</Header>
      <ScrollView>
        <Tabs selected={activeTab} onChange={handleTabChange}>
          <TabsItem value="general">{t('edit_tab_general')}</TabsItem>
          <TabsItem value="links">{t('edit_tab_links')}</TabsItem>
        </Tabs>
        {renderContent()}
      </ScrollView>
      {activeTab !== 'links' && (
        <BottomBar>
          <Button stretched onClick={handleSave}>
            {t('save')}
          </Button>
        </BottomBar>
      )}
    </>
  );
}

const EditLink: FC<EditLinkProps> = (props) => {
  const { handleBack, linkId, updateLinkInfo, link, removeLinkInfo } = props;

  const dispatch = useDispatch();
  const t = useTranslator();

  const [text, setText] = useState(link.text);
  const [url, setUrl] = useState(link.url);
  const [color, setColor] = useState(link.color || 'blue');
  const [icon, setIcon] = useState(link.icon || 'link');

  const handleColor = useCallback(
    (newColor: string) => () => {
      setColor(newColor);
    },
    [],
  );

  const handleIcon = useCallback(
    (newIcon: string) => () => {
      setIcon(newIcon);
    },
    [],
  );

  const handleText = useCallback(({ target }) => {
    setText(target.value);
  }, []);

  const handleUrl = useCallback(({ target }) => {
    setUrl(target.value);
  }, []);

  const handleSave = useCallback(() => {
    dispatch(toastActions.setToastLoading());

    const matches = getLinksRaw(url || '');
    if (!matches) {
      dispatch(toastActions.setToastFail(t('edit_link_incorrect_link')));
      return;
    }

    Api.post('/profile/links/save', {
      linkId: Math.max(0, linkId),
      color,
      text: (text || '').trim(),
      icon,
      url: matches[0].url.replace('http://', 'https://'),
    })
      .then((resp) => {
        const data: any = resp;
        updateLinkInfo(data.link);
        if (data.moderation) {
          dispatch(toastActions.hideToast());
          _confirm(t('profile_bio_moderation_alert'), () => {
            handleBack();
          });
        } else {
          dispatch(toastActions.setToastSuccess());
          handleBack();
        }
      })
      .catch((err) => {
        dispatch(toastActions.setToastFail(err.message));
      });
  }, [linkId, url, color, text, icon, t]);

  const handleDelete = () => {
    _confirm(t('edit_link_delete_confirmation'), () => {
      dispatch(toastActions.setToastLoading());
      Api.post(`/profile/links/${linkId}/delete`)
        .then((resp) => {
          dispatch(toastActions.setToastSuccess());
          removeLinkInfo(linkId);
          handleBack();
        })
        .catch((err) => {
          dispatch(toastActions.setToastFail(err.message));
        });
    });
  };

  const IconSelected = ButtonIcons[icon];

  return (
    <>
      <Header showBackButton backButtonOnClick={handleBack}>
        {linkId > 0 ? t('edit_link_title') : t('edit_link_title_new')}
      </Header>
      <ScrollView>
        <Form>
          <div className="Edit__link__preview">
            <FormItem title={t('edit_link_preview')}>
              <Button
                icon={icon === 'disable' ? undefined : <IconSelected />}
                color={color}
                mode="outline"
              >
                {text ? text.trim() : t('edit_link_btn_text_placeholder')}
              </Button>
            </FormItem>
          </div>
          <FormItem
            title={t('edit_link_btn_text')}
            description={t('edit_link_btn_text_description')}
          >
            <Input value={text} onChange={handleText} />
          </FormItem>
          <FormItem title={t('edit_link_url')}>
            <Input
              placeholder="e.g. https://ton.place/andrew"
              onChange={handleUrl}
              value={url}
            />
          </FormItem>
          <FormItem title={t('edit_link_color')}>
            <div className="Edit__link__colors__wrap">
              <div className="Edit__link__colors">
                {Colors.map((item) => (
                  <div
                    key={item}
                    className={classNames({
                      Edit__link__colors__item: true,
                      [item]: true,
                      active: item === color,
                    })}
                    onClick={handleColor(item)}
                  />
                ))}
              </div>
            </div>
          </FormItem>
          <FormItem title={t('edit_link_icon')}>
            <div className="Edit__link__icons__wrap">
              <div className="Edit__link__icons">
                {Icons.map((item) => {
                  const Icon = ButtonIcons[item];
                  return (
                    <div
                      key={item}
                      className={classNames({
                        Edit__link__icons__item: true,
                        active: item === icon,
                      })}
                      onClick={handleIcon(item)}
                    >
                      <Icon />
                    </div>
                  );
                })}
              </div>
            </div>
          </FormItem>
        </Form>
      </ScrollView>
      <BottomBar>
        <Button stretched onClick={handleSave}>
          {t('save')}
        </Button>
        {linkId > 0 && (
          <Button stretched mode="secondary" onClick={handleDelete}>
            {t('edit_link_delete')}
          </Button>
        )}
      </BottomBar>
    </>
  );
};
